import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import type {
  AppointmentsDetailsResponse,
  PreLoadAppointmentResponse,
  RescheduleAppointmentCommand,
} from '@/types/scheduling-api';

/**
 * PUT /api/Appointment/Reschedule — drag-drop / time-only reschedule.
 *
 * Wire shape (verified via dev curl): `{ appointmentId, startDate, endDate }`
 * where the dates are no-zone local datetimes (`YYYY-MM-DDTHH:mm:ss.sss`).
 * The hook re-normalises both fields through `formatLocalDatetimeForWire`
 * so callers can pass whatever shape they have (Z-suffixed, +offset, or
 * already stripped) and the wire value is always consistent. The .NET
 * model binder rejects any timezone marker.
 *
 * Cache strategy (Samer review #96 §2):
 *   - onMutate snapshots every `['appointments']` cache and the matching
 *     `['appointment-details']` entry, then patches `start`/`end` on the
 *     row in-place so the calendar tile lands on the new slot immediately.
 *   - onError restores the snapshots — tile snaps back to the source slot.
 *     The caller's toast surfaces the failure separately.
 *   - onSettled invalidates both prefix keys so the server's persisted
 *     state (which may include audit fields the optimistic patch can't
 *     predict) reconciles.
 *
 * Mirrors the optimistic pattern in `useUpdateAppointment` /
 * `useUpdateAppointmentStatus` — same snapshot-and-rollback shape so all
 * three behave identically from the cache's perspective.
 */

interface OptimisticContext {
  snapshots: { key: readonly unknown[]; data: unknown }[];
}

export function useRescheduleAppointment() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, RescheduleAppointmentCommand, OptimisticContext>({
    mutationFn: (command) =>
      put<string>('/api/Appointment/Reschedule', {
        ...command,
        startDate: formatLocalDatetimeForWire(command.startDate),
        endDate: formatLocalDatetimeForWire(command.endDate),
      }),

    onMutate: ({ appointmentId, startDate, endDate }) => {
      const snapshots: OptimisticContext['snapshots'] = [];
      const newStart = formatLocalDatetimeForWire(startDate);
      const newEnd = formatLocalDatetimeForWire(endDate);

      // 1. PreLoad cache (panel) — patch start/end on the matching row.
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['appointment-details'] })
        .forEach((q) => {
          snapshots.push({ key: q.queryKey, data: q.state.data });
        });

      queryClient.setQueriesData<PreLoadAppointmentResponse | undefined>(
        { queryKey: ['appointment-details'] },
        (prev) => {
          if (!prev?.appointment) return prev;
          if (prev.appointment.id !== appointmentId) return prev;
          return {
            ...prev,
            appointment: {
              ...prev.appointment,
              start: newStart,
              end: newEnd,
            },
          };
        },
      );

      // 2. Appointment lists (calendar) — patch the row in-place.
      queryClient
        .getQueryCache()
        .findAll({ queryKey: ['appointments'] })
        .forEach((q) => {
          snapshots.push({ key: q.queryKey, data: q.state.data });
        });

      queryClient.setQueriesData<AppointmentsDetailsResponse | undefined>(
        { queryKey: ['appointments'] },
        (prev) => {
          if (!prev?.appointments) return prev;
          const idx = prev.appointments.findIndex((a) => a.id === appointmentId);
          if (idx < 0) return prev;
          const next = prev.appointments.slice();
          next[idx] = {
            ...next[idx],
            start: newStart,
            end: newEnd,
          } as (typeof next)[number];
          return { ...prev, appointments: next };
        },
      );

      return { snapshots };
    },

    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(({ key, data }) =>
        queryClient.setQueryData(key, data),
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['appointment-details'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}
